import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelResearchRun,
  listRecipients,
  sendResearchReport,
  getResearchReport,
  getResearchRun,
  isRunActive,
  listResearchRuns,
  startResearchRun,
} from '../api/salesResearch';

/**
 * A run takes minutes, so the list polls while anything is in flight and stops
 * as soon as everything is terminal — no background traffic once the work is
 * done.
 */
const ACTIVE_POLL_MS = 3_000;

export function useResearchRuns() {
  return useQuery({
    queryKey: ['salesResearch', 'runs'],
    queryFn: listResearchRuns,
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => isRunActive(r.status)) ? ACTIVE_POLL_MS : false,
    refetchOnWindowFocus: true,
  });
}

export function useResearchRun(runId: string | null) {
  return useQuery({
    queryKey: ['salesResearch', 'run', runId],
    queryFn: () => getResearchRun(runId!),
    enabled: Boolean(runId),
    refetchInterval: (query) => (isRunActive(query.state.data?.run.status) ? ACTIVE_POLL_MS : false),
  });
}

/**
 * The report only exists once the run reaches `ready`; the endpoint returns 409
 * before then, so this stays disabled until the run says it is available.
 */
export function useResearchReport(runId: string | null, ready: boolean) {
  return useQuery({
    queryKey: ['salesResearch', 'report', runId],
    queryFn: () => getResearchReport(runId!),
    enabled: Boolean(runId) && ready,
    staleTime: Infinity,
  });
}

export function useStartResearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startResearchRun,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['salesResearch', 'runs'] });
    },
  });
}

export function useCancelResearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelResearchRun,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['salesResearch'] });
    },
  });
}

export function useRecipients(enabled: boolean) {
  return useQuery({
    queryKey: ['salesResearch', 'recipients'],
    queryFn: listRecipients,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSendReport(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { recipients: { email: string; name?: string }[]; note?: string }) =>
      sendResearchReport(runId, data),
    // The report is cached forever — it never changes — but the delivery list
    // travels with it, and that list is the record of this very send.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['salesResearch', 'report', runId] });
    },
  });
}
